import React, {useEffect, useMemo, useState} from 'react'
import UiInputText from "../components/Form/UiInputText";
import UiInputCheckbox from "../components/Form/UiInputCheckbox";
import Form from "../components/Form/Form";
import {useForm, FormProvider, useFormContext} from "react-hook-form";
import WindowBody from '../components/Window/WindowBody';
import WindowFooter from '../components/Window/WindowFooter';
import { UiButton } from '../components/UiButton';
import { ChevronDown, ChevronRight } from 'react-feather';

interface Rota {
    id: number;
    nome: string;
    acao: string;
    rotulo: string;
}

interface Entidade {
    entidade: string;
    rotaList: Rota[];
}

interface Modulo {
    modulo: string;
    entidadeList: Entidade[];
}

// O que o dono vê no nó de módulo. Prefixo de tabela é o que o sistema usa; a
// tela mostra o nome do módulo, e o que não estiver aqui aparece como veio.
const moduloRotulo: Record<string, string> = {
    un: 'Núcleo',
    pr: 'Produto',
    fat: 'Faturamento',
    os: 'Obras',
};

type Estado = 'nenhum' | 'parcial' | 'todos';

const estadoDe = (ids: number[], marcados: Set<number>): Estado => {
    if (ids.length === 0) return 'nenhum';
    const quantos = ids.filter((id) => marcados.has(id)).length;
    if (quantos === 0) return 'nenhum';
    return quantos === ids.length ? 'todos' : 'parcial';
};

// Checkbox de nó pai: `indeterminate` não é atributo, só propriedade do DOM, e é
// por isso que ele entra por ref em vez de por prop.
function CaixaDeNo({ estado, onChange, id }: { estado: Estado; onChange: () => void; id: string }) {
    const ref = React.useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (ref.current) ref.current.indeterminate = estado === 'parcial';
    }, [estado]);

    return (
        <input
            ref={ref}
            id={id}
            type="checkbox"
            checked={estado === 'todos'}
            onChange={onChange}
            style={{ margin: 0, cursor: 'pointer' }}
        />
    );
}

/**
 * A árvore de rotas: módulo, entidade, ação.
 *
 * Começa recolhida de propósito. São centenas de rotas, e abrir tudo deixaria a
 * janela inutilizável na primeira renderização.
 *
 * Marcar um nó pai marca os filhos e desmarcar desmarca; pai com parte dos filhos
 * marcados fica indeterminado. O estado de verdade é um `Set` de ids de rota, e
 * não um espelho da árvore: com um conjunto, "marcado" é uma pergunta com uma
 * resposta em qualquer nível.
 */
function ArvoreDeRotas({ arvore, marcados, alternar }: {
    arvore: Modulo[];
    marcados: Set<number>;
    alternar: (ids: number[], marcar: boolean) => void;
}) {
    const [abertos, setAbertos] = useState<Record<string, boolean>>({});

    const abrir = (chave: string) => setAbertos((prev) => ({ ...prev, [chave]: !prev[chave] }));

    if (arvore.length === 0) {
        return (
            <div className="alert alert-warning">
                Nenhuma rota no catálogo. Rode <code>php cli.php rotas:descobrir</code> no backend.
            </div>
        );
    }

    return (
        <div style={{ border: '1px solid #DDD', borderRadius: 4, maxHeight: 420, overflowY: 'auto', fontSize: 12 }}>
            {arvore.map((modulo) => {
                const idsModulo = modulo.entidadeList.flatMap((e) => e.rotaList.map((r) => r.id));
                const estadoModulo = estadoDe(idsModulo, marcados);
                const chaveModulo = `m:${modulo.modulo}`;
                const moduloAberto = !!abertos[chaveModulo];

                return (
                    <div key={modulo.modulo}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: '#F1F3F5', borderTop: '1px solid #E4E7EB' }}>
                            <span onClick={() => abrir(chaveModulo)} style={{ cursor: 'pointer', display: 'flex' }}>
                                {moduloAberto ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                            </span>
                            <CaixaDeNo
                                id={chaveModulo}
                                estado={estadoModulo}
                                onChange={() => alternar(idsModulo, estadoModulo !== 'todos')}
                            />
                            <label htmlFor={chaveModulo} style={{ margin: 0, fontWeight: 600, cursor: 'pointer' }}>
                                {moduloRotulo[modulo.modulo] || modulo.modulo}
                            </label>
                            <span className="text-muted">
                                {idsModulo.filter((id) => marcados.has(id)).length}/{idsModulo.length}
                            </span>
                        </div>

                        {moduloAberto && modulo.entidadeList.map((entidade) => {
                            const idsEntidade = entidade.rotaList.map((r) => r.id);
                            const estadoEntidade = estadoDe(idsEntidade, marcados);
                            const chaveEntidade = `e:${modulo.modulo}:${entidade.entidade}`;
                            const entidadeAberta = !!abertos[chaveEntidade];

                            return (
                                <div key={chaveEntidade}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px 3px 26px', borderTop: '1px solid #F1F3F5' }}>
                                        <span onClick={() => abrir(chaveEntidade)} style={{ cursor: 'pointer', display: 'flex' }}>
                                            {entidadeAberta ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}
                                        </span>
                                        <CaixaDeNo
                                            id={chaveEntidade}
                                            estado={estadoEntidade}
                                            onChange={() => alternar(idsEntidade, estadoEntidade !== 'todos')}
                                        />
                                        <label htmlFor={chaveEntidade} style={{ margin: 0, cursor: 'pointer' }}>
                                            {entidade.entidade}
                                        </label>
                                        <span className="text-muted">
                                            {idsEntidade.filter((id) => marcados.has(id)).length}/{idsEntidade.length}
                                        </span>
                                    </div>

                                    {entidadeAberta && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, padding: '2px 8px 6px 52px' }}>
                                            {entidade.rotaList.map((rota) => (
                                                <label
                                                    key={rota.id}
                                                    title={rota.nome}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 4,
                                                        margin: 0,
                                                        padding: '1px 6px',
                                                        minWidth: 150,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={marcados.has(rota.id)}
                                                        onChange={() => alternar([rota.id], !marcados.has(rota.id))}
                                                        style={{ margin: 0 }}
                                                    />
                                                    {rota.rotulo}
                                                    <span className="text-muted" style={{ fontSize: 10 }}>{rota.acao}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}

function CampoDeRotas() {
    const { watch, setValue } = useFormContext();
    const arvore: Modulo[] = watch('rotaArvore') || [];
    const ids = watch('rotaIds');

    const marcados = useMemo(
        () => new Set<number>(Array.isArray(ids) ? ids.map(Number) : []),
        [ids]
    );

    const alternar = (alvos: number[], marcar: boolean) => {
        const proximo = new Set<number>(marcados);
        alvos.forEach((id) => (marcar ? proximo.add(id) : proximo.delete(id)));
        setValue('rotaIds', Array.from(proximo).sort((a, b) => a - b), { shouldDirty: true });
    };

    const total = arvore.reduce((soma, m) => soma + m.entidadeList.reduce((s, e) => s + e.rotaList.length, 0), 0);

    return (
        <div className="col-lg-12">
            <div className="form-item">
                <label className="form-label">
                    Rotas liberadas ({marcados.size} de {total})
                    {marcados.size > 0 && (
                        <button
                            type="button"
                            className="btn btn-sm btn-link"
                            onClick={() => setValue('rotaIds', [], { shouldDirty: true })}
                        >
                            desmarcar todas
                        </button>
                    )}
                </label>
                <ArvoreDeRotas arvore={arvore} marcados={marcados} alternar={alternar}/>
            </div>
        </div>
    );
}

export default function PerfilForm(props: any){
    const methods = useForm({
        defaultValues: props.data || { ativo: true, rotaIds: [], rotaArvore: [] }
    });
    const { handleSubmit, reset } = methods;

    useEffect(() => {
        if (props.data) {
            reset(props.data);
        }
    }, [props.data, reset]);

    /**
     * `convertModelToFormData` pula valor falsy e não serializa array de
     * escalares, então a lista vai como texto e nunca vazia de verdade:
     * `rotaIdsEnviado` sempre acompanha, e é ele que autoriza o servidor a mexer
     * nos vínculos. Sem esse par, desmarcar todas sumiria no caminho e viraria
     * "não mexi".
     */
    const salvar = (formData: any) => {
        const ids: number[] = Array.isArray(formData.rotaIds) ? formData.rotaIds.map(Number) : [];

        const { rotaArvore, usuarios, ...resto } = formData;

        props.triggerSubmit({
            ...resto,
            rotaIds: ids.join(','),
            rotaIdsEnviado: '1',
        });
    };

    return (
        <FormProvider {...methods}>
            <Form onSubmit={handleSubmit(salvar)} methods={methods}>
                <WindowBody>
                    <div className="row">
                        <UiInputText size={10} name="nome" label="Nome"/>
                        <UiInputCheckbox size={2} name="ativo" label="Ativo"/>
                    </div>

                    <fieldset>
                        <legend>Acesso por rota</legend>
                        <div className="row">
                            <CampoDeRotas/>
                        </div>
                    </fieldset>
                </WindowBody>

                <WindowFooter>
                    <UiButton variant="secondary" onClick={props.triggerClose}>Fechar</UiButton>
                    <UiButton variant="primary" type="submit">Salvar</UiButton>
                </WindowFooter>
            </Form>
        </FormProvider>
    )
}
